import React from "react";
import { Helmet } from "react-helmet-async";
import Container from "../components/layout/Container";
import PublicHero from "../components/PublicHero";
import OfferCards from "../components/offers/OfferCards";
import SubNav, {
  veiligheidsdienstenSubNavItems,
} from "../components/SubNav";

const pageUrl = "https://missionmovement.nl/programma-veiligheidsdiensten";
const heroImage = "/img/eenheden/veiligheidsdiensten.jpg";
const ogImage =
  "https://missionmovement.nl/img/eenheden/veiligheidsdiensten.jpg";

const benefits = [
  {
    title: "Intake & startniveau",
    text: "We brengen je doel, ervaring, huidige niveau, belastbaarheid, beschikbare tijd en praktische situatie in kaart.",
  },
  {
    title: "Richting & functie-eisen",
    text: "Je voorbereiding sluit aan op jouw route, jouw startniveau en de fysieke belasting die bij jouw doel past.",
  },
  {
    title: "Belastbaarheid & progressie",
    text: "De focus ligt op kracht, conditie, mobiliteit, herstel, techniek en het vermogen om training stap voor stap beter te verdragen.",
  },
  {
    title: "Follow-up & bijsturing",
    text: "We houden contact tijdens het traject en sturen bij op voortgang, herstel, uitvoering en praktische vragen.",
  },
];

const faqItems = [
  {
    question: "Is dit programma hetzelfde als het Defensie-programma?",
    answer:
      "Nee. Het Defensie-programma is meer gestructureerd rond militaire voorbereiding. Het traject voor veiligheidsdiensten is persoonlijker en wordt afgestemd op jouw richting, startniveau en doel.",
  },
  {
    question: "Voor welke veiligheidsdiensten is dit geschikt?",
    answer:
      "Geschikt voor kandidaten richting politie, brandweer, KMar, handhaving, beveiliging of andere functies waar fysieke en mentale voorbereiding belangrijk is.",
  },
  {
    question: "Hoe persoonlijk is dit traject?",
    answer:
      "Voor veiligheidsdiensten ligt de nadruk op intake, persoonlijk trainingsplan, follow-ups en begeleiding tijdens je voorbereiding. De accenten verschillen per richting, functie en fysieke eisen.",
  },
  {
    question: "Kan ik starten als ik nog niet fit genoeg ben?",
    answer:
      "Ja. Je hoeft nog niet op niveau te zijn om te starten. Het belangrijkste is dat je eerlijk kijkt naar waar je nu staat en bereid bent om gericht te werken aan wat nodig is. Twijfel je over pijnsignalen, eerdere blessures of belastbaarheid? Benoem dit tijdens de intake, zodat de opbouw daarop kan worden afgestemd.",
  },
  {
    question: "Wat krijg ik concreet bij het programma Veiligheidsdiensten?",
    answer:
      "Je krijgt een intake, een persoonlijk trainingsplan, duidelijke trainingsrichting en follow-ups tijdens je voorbereiding. De focus ligt op kracht, conditie, belastbaarheid, herstel, mentale weerbaarheid en structuur richting jouw selectie, keuring of opleiding.",
  },
];

const webPageSchema = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Programma Veiligheidsdiensten",
  url: pageUrl,
  inLanguage: "nl-NL",
  description:
    "Persoonlijke begeleiding en training voor kandidaten richting politie, brandweer, KMar en andere veiligheidsdiensten.",
  publisher: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
};

const serviceSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Programma Veiligheidsdiensten",
  url: pageUrl,
  provider: {
    "@type": "Organization",
    name: "Mission Movement",
    url: "https://missionmovement.nl/",
  },
  areaServed: {
    "@type": "Country",
    name: "Netherlands",
  },
  serviceType: "Persoonlijke training en begeleiding voor veiligheidsdiensten",
  audience: {
    "@type": "Audience",
    audienceType:
      "Kandidaten richting politie, brandweer, KMar, handhaving, beveiliging en veiligheidsdiensten",
  },
  description:
    "Mission Movement helpt kandidaten richting veiligheidsdiensten bouwen aan kracht, conditie, belastbaarheid, discipline en mentale voorbereiding met een persoonlijk trainingsplan en follow-ups.",
};

const faqPageSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqItems.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

const ProgrammaVeiligheidsdiensten = () => {
  return (
    <main className="min-h-screen bg-mmPage text-mmText">
      <Helmet>
        <title>
          Programma Veiligheidsdiensten | Politie, Brandweer & KMar
          Voorbereiding
        </title>
        <meta
          name="description"
          content="Persoonlijke begeleiding en training voor kandidaten richting politie, brandweer, KMar en andere veiligheidsdiensten. Bouw aan kracht, conditie, belastbaarheid en discipline."
        />
        <meta
          property="og:title"
          content="Programma Veiligheidsdiensten | Mission Movement"
        />
        <meta
          property="og:description"
          content="Mission Movement begeleidt kandidaten richting politie, brandweer, KMar en veiligheidsdiensten met intake, persoonlijk trainingsplan en follow-ups."
        />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:url" content={pageUrl} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(serviceSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(faqPageSchema)}
        </script>
      </Helmet>

      <PublicHero
        eyebrow="Veiligheidsdiensten-programma"
        title="Programma Veiligheidsdiensten"
        subtitle="Een persoonlijk 12-weeks traject als jij richting politie, brandweer, KMar of een andere fysiek en mentaal veeleisende functie werkt."
        button={{ label: "Start je voorbereiding", to: "/pricing" }}
        imageSrc={heroImage}
        imageAlt="Veiligheidsdiensten voorbereiding"
        imageClassName="object-center"
        compact
      />

      <div className="relative shadow-sm">
        <SubNav
          variant="dark"
          scrollable
          align="center"
          items={veiligheidsdienstenSubNavItems}
        />
      </div>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">Voor wie is dit programma?</h2>
            <div className="space-y-6 text-mmTextMuted text-base md:text-lg leading-relaxed">
              <p>
                Het programma Veiligheidsdiensten is bedoeld voor mensen die
                serieus richting politie, brandweer, KMar of een andere fysiek
                en mentaal veeleisende functie willen werken.
              </p>
              <p>
                De basis blijft hetzelfde: startniveau bepalen, gericht
                opbouwen, belastbaarheid vergroten en bijsturen waar nodig. De
                accenten verschillen per richting, functie en fysieke eisen.
              </p>
              <p>
                Voor politie, brandweer, KMar en andere veiligheidsdiensten
                werken we via persoonlijke begeleiding. Het traject wordt
                afgestemd op jouw startniveau, doel en functie-eisen.
              </p>
              <p>
                We kijken waar je nu staat, waar je naartoe wilt en wat er nodig
                is om daar gestructureerd aan te bouwen.
              </p>
            </div>
            <p className="mt-8 border-l-2 border-mmAccent pl-5 text-mmTextMuted text-base md:text-lg leading-relaxed">
              Voor veiligheidsdiensten verschilt de route per functie. Politie,
              brandweer, KMar, handhaving en beveiliging vragen niet allemaal
              exact hetzelfde. Daarom is dit traject opgebouwd rond jouw doel,
              jouw startniveau en de belasting waar je naartoe werkt.
            </p>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <h2 className="mm-h2 mb-4">Wat krijg je?</h2>
          <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-3xl mb-10">
            Een persoonlijk 12-weeks traject dat aansluit op jouw richting,
            startniveau, functie-eisen en belastbaarheid.
          </p>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {benefits.map((item, index) => (
              <article key={item.title} className="mm-card p-6">
                <div className="h-9 w-9 rounded-full border border-mmBorderStrong flex items-center justify-center text-mmAccent font-semibold mb-5">
                  {index + 1}
                </div>
                <h3 className="font-display uppercase tracking-widest text-mmAccent text-base mb-3">
                  {item.title}
                </h3>
                <p className="text-mmTextMuted text-sm leading-relaxed">
                  {item.text}
                </p>
              </article>
            ))}
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-6">Voor welke richtingen?</h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed">
              Dit traject is bedoeld voor mensen die richting politie,
              brandweer, Koninklijke Marechaussee, handhaving, beveiliging of
              andere functies werken waar fysieke en mentale voorbereiding
              belangrijk is.
            </p>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20 bg-mmSurface border-b border-mmBorder">
        <Container>
          <div className="max-w-4xl">
            <h2 className="mm-h2 mb-8">Veelgestelde vragen</h2>
            <div className="space-y-4">
              {faqItems.map((item) => (
                <article key={item.question} className="mm-card p-6">
                  <h3 className="font-display uppercase tracking-widest text-mmAccent text-base md:text-lg mb-3">
                    {item.question}
                  </h3>
                  <p className="text-mmTextMuted text-base leading-relaxed">
                    {item.answer}
                  </p>
                </article>
              ))}
            </div>
          </div>
        </Container>
      </section>

      <section className="py-16 md:py-20">
        <Container>
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="mm-h2 mb-5">
              Start gericht richting jouw functie
            </h2>
            <p className="text-mmTextMuted text-base md:text-lg leading-relaxed max-w-3xl mx-auto">
              Train met persoonlijke begeleiding op basis van je startniveau,
              functie-eisen en belastbaarheid.
            </p>
          </div>
        </Container>
        <OfferCards variant="veiligheidsdiensten" context="embedded" />
      </section>
    </main>
  );
};

export default ProgrammaVeiligheidsdiensten;
